import { call, put, select } from 'redux-saga/effects';
import * as actions from './actions';
import * as eventReportActions from 'store/eventReports/actions';
import axios from 'axios';
import { Base64 } from 'js-base64';
import { produce } from 'immer';
import { updateSchemaFilters } from './actions';
import { sagas as eventSagas } from 'store/event';
import { getParams } from 'store/helpers/helperFunctions';
import { excludeProductNames } from 'constants.js';
const { waitForGetSupportedEventTypes } = eventSagas;

const decodeExampleContent = (schema) => {
  try {
    if (schema.examplePayload && schema.examplePayload !== '') {
      schema.examplePayload = Base64.decode(schema.examplePayload);
    }
  } catch (ex) {
    schema.examplePayload = '';
  }
  return schema;
};

const getUniqueEventIdentifierValues = (eventIdentifierValues) => {
  let uniqueEventIdentifierFields = [];
  eventIdentifierValues.eventIdentifierProductData.forEach((productData) => {
    productData.eventIdentifierData.forEach((identifierData) => {
      identifierData.eventIdentifiers.forEach((identifier) => {
        if (identifier.eventIdentifierFields) {
          identifier.eventIdentifierFields.forEach((field) => {
            if (field && field.length > 0) {
              uniqueEventIdentifierFields.push(field);
            }
          });
        }
      });
    });
  });
  return [...new Set(uniqueEventIdentifierFields)];
};
const apiRoot = `${window._env_?.REACT_APP_SCHEMA_API_PATH}`;

const getSchemaApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}tenants/${tenantId}/context/${businessContextId}`;

let isArchiveInProgress = false;

export const getSchemasAsync = (tenantId, businessContextId, showActive) =>
  axios
    .get(
      `${getSchemaApiRoot(
        tenantId,
        businessContextId
      )}/schema/search?index=0&pageSize=99&active=${showActive}&productCategory&productName&eventTypeCategory&eventType`
    )
    .then((response) => response.data)
    .catch((err) => {
      throw err;
    });

export const getPaginatedSchemasAsync = (
  tenantId,
  businessContextId,
  urlParams,
  bodyParams
) => {
  return axios
    .post(
      `${getSchemaApiRoot(tenantId, businessContextId)}/schema/search?index=${
        urlParams.index
      }&pageSize=${urlParams.pageSize}`,
      bodyParams
    )
    .then((response) => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch((err) => {
      throw err;
    });
};

export const getSchemaAsync = (tenantId, businessContextId, schemaId) =>
  axios
    .get(`${getSchemaApiRoot(tenantId, businessContextId)}/schema/${schemaId}`)
    .then((response) => response.data)
    .catch((err) => {
      throw err;
    });

export const getSchemaKeysAsync = (tenantId, businessContextId) =>
  axios
    .get(`${getSchemaApiRoot(tenantId, businessContextId)}/schema/fields`)
    .then((response) => response.data)
    .catch((err) => {
      throw err;
    });

const getEventTypesAsync = () =>
  axios
    .get(`${apiRoot}eventType`)
    .then((response) => response.data)
    .catch((err) => {
      throw err;
    });

export const createSchemaAsync = (tenantId, businessContextId, schema) =>
  axios
    .post(
      `${getSchemaApiRoot(
        tenantId,
        businessContextId
      )}/schema/configuration?productName=${schema.eventProductName}`,
      schema
    )
    .then((response) => response.data)
    .catch((err) => {
      throw err;
    });

export const updateSchemaAsync = (
  tenantId,
  businessContextId,
  schema,
  schemaId
) =>
  axios
    .put(
      `${getSchemaApiRoot(
        tenantId,
        businessContextId
      )}/schema/configuration/${schemaId}?productName=${
        schema.eventProductName
      }`,
      schema
    )
    .then((response) => response.data)
    .catch((err) => {
      throw err;
    });

export const activateSchemaAsync = (tenantId, businessContextId, schemaId) =>
  axios
    .put(
      `${getSchemaApiRoot(
        tenantId,
        businessContextId
      )}/schema/configuration/activate/${schemaId}`
    )
    .then((response) => response.data)
    .catch((err) => {
      throw err;
    });

export const deactivateSchemaAsync = (tenantId, businessContextId, schemaId) =>
  axios
    .put(
      `${getSchemaApiRoot(
        tenantId,
        businessContextId
      )}/schema/configuration/deactivate/${schemaId}`
    )
    .then((response) => response.data)
    .catch((err) => {
      throw err;
    });

export const getEventIdentifierValuesAsync = (
  tenantId,
  businessContextId,
  bodyParams
) => {
  return axios
    .post(
      `${getSchemaApiRoot(
        tenantId,
        businessContextId
      )}/schema/event-identifier-values`,
      bodyParams
    )
    .then((response) => response.data)
    .catch((err) => {
      throw err;
    });
};

export const getSchemaOptionsAsync = (
  tenantId,
  businessContextId,
  fieldType
) => {
  return axios
    .get(
      `${getSchemaApiRoot(
        tenantId,
        businessContextId
      )}/schema/options?fieldType=${fieldType}`
    )
    .then((response) => response.data)
    .catch((err) => {
      throw err;
    });
};
const currentTenantId = (store) => store.user.selectedTenant.tenantId;
const currentBusinessContextId = (store) =>
  store.user.selectedBusinessContext.businessContextId;
const currentSchema = (store) => (store.schema ? store.schema.current : {});
const currentEventTypes = (store) =>
  store.schema ? store.schema.eventTypes : {};
const currentList = (store) => (store.schema ? store.schema.list : []);

const paginatedSchemaList = (store) =>
  store.schema ? store.schema.paginatedList : [];
const productNames = (store) => store.events.supportedEventTypes.productNames;
const productCategories = (store) =>
  store.events.supportedEventTypes.productCategories;
const pageNo = (store) =>
  store.schema.filters ? store.schema.filters?.pageNo : 0;
const searchInput = (store) =>
  store.schema.filters ? store.schema.filters?.searchInput : '';
const sortBy = (store) =>
  store.schema.filters ? store.schema.filters?.sortBy : 'schemaName';
const sortOrder = (store) =>
  store.schema.filters ? store.schema.filters?.sortOrder : 'ASC';
const rowsPerPage = (store) =>
  store.schema.filters ? store.schema.filters?.rowsPerPage : 10;
const displayActive = (store) =>
  store.schema.filters ? store.schema.filters?.displayActive : true;
const filterProductNames = (store) =>
  store.schema.filters ? store.schema.filters?.filterProductNames : [];
const filterProductCategories = (store) =>
  store.schema.filters ? store.schema.filters?.filterProductCategories : [];
const totalCount = (store) =>
  store.schema.filters ? store.schema.filters?.totalCount : 0;

export function* getSchemasIfNeeded(action) {
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let list = yield select(currentList);
    let currentSchemaList = list;
    yield put(actions.gettingSchemas());
    if (action.payload.force || list.length === 0) {
      currentSchemaList = yield call(
        getSchemasAsync,
        tenantId,
        businessContextId,
        action.payload ? action.payload.showActive : true
      );
      currentSchemaList = currentSchemaList.map((schema) => ({
        ...schema,
        examplePayload:
          schema.examplePayload && schema.examplePayload !== ''
            ? Base64.decode(schema.examplePayload)
            : schema.examplePayload
      }));
    }
    yield put(actions.gettingSchemasSucceeded(currentSchemaList));
  } catch (ex) {
    yield put(actions.gettingSchemasFailed(ex));
  }
}

export function* getPaginatedSchemasIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentSchemaList = yield select(paginatedSchemaList);
    yield call(waitForGetSupportedEventTypes);
    let productCategory = yield select(productCategories);
    let productName = yield select(productNames);

    if (
      action.type === actions.GET_PAGINATED_SCHEMAS &&
      (currentSchemaList.length === 0 || payload.force === true)
    ) {
      const requestBody = {
        name: yield select(searchInput),
        sortBy: yield select(sortBy),
        sortOrder: yield select(sortOrder),
        active: yield select(displayActive),
        productName: yield select(filterProductNames),
        productCategory: yield select(filterProductCategories)
      };
      let urlParams = {
        index: yield select(pageNo),
        pageSize: yield select(rowsPerPage)
      };
      let bodyParams = {
        ...getParams(requestBody, productName, productCategory)
      };
      let listCount = yield select(totalCount);
      if (
        isArchiveInProgress &&
        listCount - 1 &&
        urlParams.index >= (listCount - 1) / urlParams.pageSize
      ) {
        urlParams.index = urlParams.index - 1;
        yield put(updateSchemaFilters({ pageNo: urlParams.index }));
      }
      yield put(actions.gettingPaginatedSchemas());
      currentSchemaList = yield call(
        getPaginatedSchemasAsync,
        tenantId,
        businessContextId,
        urlParams,
        bodyParams
      );
      yield put(
        updateSchemaFilters({ totalCount: currentSchemaList.totalRecords })
      );
      currentSchemaList = produce(currentSchemaList.data, (draftList) => {
        draftList = draftList.map(decodeExampleContent);
      });
    }
    isArchiveInProgress = false;
    yield put(actions.gettingPaginatedSchemasSucceeded(currentSchemaList));
  } catch (ex) {
    isArchiveInProgress = false;
    yield put(updateSchemaFilters({ totalCount: 0 }));
    yield put(actions.gettingPaginatedSchemasFailed(ex));
  }
}

export function* getSchemaKeys() {
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    yield put(actions.gettingSchemaKeys());
    let schemaKeys = yield call(
      getSchemaKeysAsync,
      tenantId,
      businessContextId
    );
    yield put(actions.gettingSchemaKeysSucceeded(schemaKeys));
  } catch (ex) {
    yield put(actions.gettingSchemaKeysFailed(ex));
  }
}

export function* getSchemaIfNeeded(action) {
  const { payload } = action;
  try {
    yield put(actions.gettingSchema());
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let schema = yield select(currentSchema);
    let schemaId = payload.schemaId;
    if (
      !schema ||
      (schema && schema.id !== schemaId) ||
      payload.force === true
    ) {
      schema = yield call(
        getSchemaAsync,
        tenantId,
        businessContextId,
        schemaId
      );
    }
    yield put(actions.gettingSchemaSucceeded(schema));
  } catch (ex) {
    yield put(actions.gettingSchemaFailed(ex));
  }
}

export function* getEventTypesIfNeeded() {
  try {
    const eventTypes = yield select(currentEventTypes);
    if (!eventTypes) {
      let eventTypes = yield call(getEventTypesAsync);
      yield put(actions.gettingEventTypesSucceeded(eventTypes));
    }
  } catch (ex) {
    yield put(actions.gettingEventTypesFailed(ex));
  }
}

export function* createSchemaFromCurrent() {
  try {
    yield put(actions.creatingSchema());
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let schema = yield select(currentSchema);
    if (!schema.id) {
      schema = yield call(
        createSchemaAsync,
        tenantId,
        businessContextId,
        schema
      );
    }
    yield put(actions.creatingSchemaSucceeded(schema));
  } catch (ex) {
    yield put(actions.creatingSchemaFailed({ isValidationFailed: false }));
  }
}

export function* updateSchemaFromCurrent(action) {
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let schema = yield select(currentSchema);
    schema = yield call(
      updateSchemaAsync,
      tenantId,
      businessContextId,
      schema,
      action.payload.schemaId
    );
    if (!schema.id) {
      schema = { ...schema, id: action.payload.schemaId };
    }
    yield put(actions.updatingSchemaSucceeded(schema));
  } catch (ex) {
    yield put(actions.updatingSchemaFailed({ isValidationFailed: false }));
  }
}

export function* activateOrDeactivateSchema(action) {
  let { payload } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let success = false;
    if (payload.activeStatus) {
      success = yield call(
        deactivateSchemaAsync,
        tenantId,
        businessContextId,
        payload.schemaId
      );
    } else {
      success = yield call(
        activateSchemaAsync,
        tenantId,
        businessContextId,
        payload.schemaId
      );
    }
    isArchiveInProgress = true;
    yield put(actions.activateOrDeactivateSchemaSucceeded(success));
  } catch (ex) {
    isArchiveInProgress = false;
    yield put(
      actions.activateOrDeactivateSchemaFailed({
        activeStatus: payload.activeStatus
      })
    );
  }
}

export function* getEventIdentifierValuesIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let productName = yield select(productNames);
    let productNameOptions = productName
      ? productName.filter((name) => !excludeProductNames.includes(name))
      : [];
    let eventIdentifierValues = yield call(
      getEventIdentifierValuesAsync,
      tenantId,
      businessContextId,
      payload.filteredProductName.length > 0
        ? payload.filteredProductName
        : productNameOptions || []
    );
    yield put(
      actions.gettingEventIdentifierValuesSucceeded(
        getUniqueEventIdentifierValues(eventIdentifierValues)
      )
    );
  } catch (ex) {
    yield put(eventReportActions.gettingPaginatedEventReportsFailed(ex));
  }
}

export function* getSchemaOptionsIfNeeded(action) {
  try {
    const tenantId = yield select(currentTenantId);
    const businessContextId = yield select(currentBusinessContextId);

    const options = yield call(
      getSchemaOptionsAsync,
      tenantId,
      businessContextId,
      action.payload.fieldType
    );

    yield put(
      actions.gettingSchemaOptionsSucceeded({
        fieldType: action.payload.fieldType,
        options
      })
    );
  } catch (ex) {
    yield put(actions.gettingSchemaOptionsFailed(ex));
  }
}
